import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  APIError,
  redactValue,
  setApiErrorReporter,
} from "@coras-io/embed/errors";

import App from "./App";
import { router } from "./router";
import "./index.css";

const reportedApiErrors = new WeakSet<APIError>();

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.2,
    ),
    profilesSampleRate: Number(
      import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE || 0.2,
    ),
    normalizeDepth: 6,
    ignoreErrors: [
      /CSSStyleSheet\.cssRules getter.*cross-origin stylesheet/,
      /Failed to fetch dynamically imported module/,
      /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
    ],
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["warn", "error"] }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof APIError && reportedApiErrors.has(error)) {
        return null;
      }
      return event;
    },
  });

  setApiErrorReporter((error) => {
    Sentry.withScope((scope) => {
      scope.setLevel(
        error.statusCode && error.statusCode >= 500 ? "error" : "warning",
      );
      scope.setFingerprint([
        "api-error",
        error.method,
        error.endpoint,
        String(error.statusCode),
      ]);
      scope.setContext("apiError", {
        endpoint: error.endpoint,
        method: error.method,
        statusCode: error.statusCode,
        response: JSON.stringify(redactValue(error.response)),
      });
      Sentry.captureException(error);
    });
    reportedApiErrors.add(error);
  });
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });

  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary>
        <App />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}
